import { ChartColumn, Church, TrendingUp } from 'lucide-react';

import { Badge } from '@/design-system/ui/Badge';

import SlideCopy, { FloatingNote } from './SlideCopy';

/** Membros ativos no fim de cada mês — ilustrativo, como a escala de exemplo ao lado. */
const ACTIVE = [212, 215, 214, 219, 223, 222, 228, 231, 236, 239, 244, 248];
const MONTHS = ['nov', 'jan', 'mar', 'mai', 'jul', 'out'];
const STATS = [
  { label: 'Novos membros', value: '+41' },
  { label: 'Público médio', value: '312' },
  { label: 'Visitantes', value: '54' },
];

const W = 320;
const H = 72;
const PAD = 6;

/** A curva da evolução no espaço do desenho (de cima para baixo, com folga nas bordas). */
function curve(values: number[]) {
  const min = Math.min(...values) - 6;
  const max = Math.max(...values) + 4;
  const points = values.map((v, i) => [
    PAD + (i * (W - PAD * 2)) / (values.length - 1),
    PAD + ((max - v) * (H - PAD * 2)) / (max - min),
  ]);
  const line = points.map(([x, y], i) => `${i ? 'L' : 'M'}${x?.toFixed(1)} ${y?.toFixed(1)}`).join(' ');

  return { line, area: `${line} L${W - PAD} ${H} L${PAD} ${H} Z`, last: points[points.length - 1] ?? [0, 0] };
}

const CHART = curve(ACTIVE);

/** Os pontos de calor do mapa em miniatura: onde moram mais membros (posição e intensidade). */
const HEAT = [
  { x: 38, y: 30, r: 22 },
  { x: 74, y: 52, r: 26 },
  { x: 92, y: 24, r: 16 },
  { x: 30, y: 62, r: 14 },
];

/**
 * Destaque da Membresia: a evolução dos membros ativos e, no cartão flutuante, o mapa da
 * distribuição geográfica. É ilustração (fica escondida do leitor de tela, o texto conta),
 * por isso o desenho é SVG solto e não um `EChart` — o login não carrega o eCharts.
 */
export default function MembershipSlide({ active }: { active: boolean }) {
  const [lastX, lastY] = CHART.last;

  return (
    <>
      <SlideCopy
        icon={ChartColumn}
        tag="Membresia em números"
        title="Veja sua igreja crescer, mês a mês."
        text="Evolução dos membros, público dos eventos, visitantes e o mapa dos bairros, calculados dos seus próprios cadastros e registros."
      />

      <div aria-hidden className="flex max-w-115 flex-col gap-3">
        <div className="overflow-hidden rounded-card bg-white text-ink shadow-dialog">
          <div className="flex items-start justify-between gap-3 px-4 pt-3.5">
            <div>
              <p className="m-0 text-meta text-ink-subtle">Membros ativos</p>
              <p data-numeric className="m-0 text-display leading-tight font-extrabold tracking-[-0.02em]">
                248
              </p>
            </div>
            <Badge tone="complete">
              <TrendingUp />
              +17% em 12 meses
            </Badge>
          </div>

          <div className="mt-3 grid grid-cols-3 border-y border-line-divider">
            {STATS.map((s) => (
              <div key={s.label} className="border-l border-line-divider px-4 py-2.5 first:border-l-0">
                <p className="m-0 text-caption text-ink-subtle">{s.label}</p>
                <p data-numeric className="m-0 text-body font-bold">
                  {s.value}
                </p>
              </div>
            ))}
          </div>

          <div className="px-4 pt-3 pb-9">
            <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full overflow-visible text-primary">
              <defs>
                <linearGradient id="showcase-growth" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
                  <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                </linearGradient>
              </defs>
              {[0.25, 0.5, 0.75].map((f) => (
                <line
                  key={f}
                  x1={0}
                  x2={W}
                  y1={H * f}
                  y2={H * f}
                  className="text-line-divider"
                  stroke="currentColor"
                  strokeDasharray="3 4"
                />
              ))}
              <path
                d={CHART.area}
                fill="url(#showcase-growth)"
                className={`transition-opacity delay-300 duration-700 ${active ? 'opacity-100' : 'opacity-0'}`}
              />
              <path
                d={CHART.line}
                pathLength={1}
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={1}
                className="transition-[stroke-dashoffset] delay-150 duration-1200 ease-out"
                style={{ strokeDashoffset: active ? 0 : 1 }}
              />
              <circle cx={lastX} cy={lastY} r={9} fill="currentColor" opacity={0.15} />
              <circle
                cx={lastX}
                cy={lastY}
                r={4}
                fill="currentColor"
                strokeWidth={2}
                className="stroke-white"
              />
            </svg>
            <div className="flex justify-between pt-1 text-micro text-ink-subtle">
              {MONTHS.map((m) => (
                <span key={m}>{m}</span>
              ))}
            </div>
          </div>
        </div>

        <FloatingNote>
          <svg
            viewBox="0 0 120 80"
            className="block h-14 w-21 overflow-hidden rounded-control bg-surface-muted"
          >
            <defs>
              <radialGradient id="showcase-heat">
                <stop offset="0%" className="text-accent-amber" stopColor="currentColor" stopOpacity="0.9" />
                <stop offset="45%" className="text-primary" stopColor="currentColor" stopOpacity="0.45" />
                <stop offset="100%" className="text-primary" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>
            </defs>
            <g className="text-white" stroke="currentColor" strokeWidth={3} fill="none" strokeLinecap="round">
              <path d="M-4 46 C30 40 60 58 124 44" />
              <path d="M52 -4 C48 28 62 54 56 84" />
              <path d="M-4 14 L124 30" strokeWidth={2} />
              <path d="M88 -4 L104 84" strokeWidth={2} />
            </g>
            {HEAT.map((h) => (
              <circle key={`${h.x}-${h.y}`} cx={h.x} cy={h.y} r={h.r} fill="url(#showcase-heat)" />
            ))}
          </svg>
          <div>
            <p className="m-0 flex items-center gap-1 text-caption text-ink-subtle">
              <Church className="size-3 stroke-2 text-primary" />
              Distribuição geográfica
            </p>
            <p className="m-0 text-body font-bold">Membros por bairro, no mapa</p>
          </div>
        </FloatingNote>
      </div>
    </>
  );
}
